import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const Navbar = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { language, switchLanguage, t } = useLanguage();

  const navLinks = [
    { path: "/", name: t("navbar.home") },
    { path: "/services", name: t("navbar.services") },
    { path: "/blog", name: t("navbar.blog") },
    { path: "/contact", name: t("navbar.contact") },
  ];

  const isActiveLink = (path) =>
    path === "/blog" ? location.pathname.startsWith("/blog") : location.pathname === path;

  const languageOptions = [
    { code: "en", label: "EN" },
    { code: "uk", label: "UK" },
  ];

  return (
    <nav className="bg-gradient-to-b from-gray-100 to-white text-gray-900 shadow-md fixed w-full z-50">
      <div className="container mx-auto px-6 py-4 flex justify-between items-center relative">
        <Link to="/" className="flex items-center">
          <img src="/assets/logos/mini_logo.png" alt={t("common.logoAlt")} className="h-12 w-auto" />
        </Link>

        <div className="absolute left-1/2 transform -translate-x-1/2 text-center hidden md:block">
          <p className="text-lg font-bold uppercase leading-tight text-gray-400 transition-none">
            {t("navbar.brandLine1")} <br /> {t("navbar.brandLine2")}
          </p>
        </div>

        <div className="hidden md:flex items-center gap-6 ml-auto">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-lg font-medium transition-all duration-300 ${
                isActiveLink(link.path)
                  ? "text-yellow-500 border-b-2 border-yellow-500"
                  : "text-gray-700 hover:text-yellow-500"
              }`}
            >
              {link.name}
            </Link>
          ))}

          <div className="flex items-center gap-2 border border-gray-300 rounded-full px-2 py-1">
            {languageOptions.map((option) => (
              <button
                key={option.code}
                type="button"
                aria-label={`${t("navbar.language")}: ${option.label}`}
                onClick={() => switchLanguage(option.code)}
                className={`px-3 py-1 rounded-full text-sm font-semibold transition ${
                  language === option.code ? "bg-yellow-500 text-black" : "text-gray-700 hover:bg-gray-200"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <button className="md:hidden text-gray-800" onClick={() => setMenuOpen(!menuOpen)} aria-label="menu">
          {menuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {menuOpen && (
        <>
          <button
            type="button"
            aria-label="Close mobile menu"
            onClick={() => setMenuOpen(false)}
            className="md:hidden fixed inset-0 top-16 bg-black/20 z-40"
          />

          <div className="md:hidden bg-gray-300 shadow-md absolute top-16 left-0 w-full border-t border-gray-400 z-50">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-6 py-4 text-lg transition-all duration-300 ${
                  isActiveLink(link.path) ? "text-yellow-500 bg-gray-200" : "text-gray-700 hover:bg-gray-300"
                }`}
                onClick={() => setMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}

            <div className="px-6 py-4 border-t border-gray-400 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">{t("navbar.language")}</span>
              <div className="flex items-center gap-2">
                {languageOptions.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    onClick={() => {
                      switchLanguage(option.code);
                      setMenuOpen(false);
                    }}
                    className={`px-3 py-1 rounded-full text-sm font-semibold transition ${
                      language === option.code ? "bg-yellow-500 text-black" : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default Navbar;
